'use client';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { PmNoMessageIcon } from 'src/assets/images/job-details-slide-out/job-details-slide-out';
import TenantSidebar from 'src/components/job-slide-out/components/nav-table/tenant-lula-chat/tenant-sidebar';
import TextThread, {
	type TenantTextsStatus,
} from 'src/components/job-slide-out/components/nav-table/tenant-lula-chat/text-thread';
import { createAbortSignal } from 'src/helpers/utils';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import useInfiniteScroll from 'src/hooks/useInfiniteScroll';
import {
	fetchTenantContactMessages,
	fetchTenantContacts,
} from 'src/redux/jobs/api';
import type {
	TenantContact,
	TenantContactMessage,
	TenantContactMessagesResponse,
	TenantContactsResponse,
} from 'src/types';
import {
	MOCK_CONTACTS,
	MOCK_MESSAGES,
} from 'src/components/job-slide-out/components/nav-table/tenant-lula-chat/mock-messages';

// DEV-ONLY review toggle — unset (default) means OFF; set NEXT_PUBLIC_TENANT_TEXTS_MOCK=true to enable.
const USE_TENANT_TEXTS_MOCK = false;

// VERBATIM copy — do not edit (AC2). Always-on, non-live region.
const WARNING_COPY =
	'This thread shows all messages between this tenant and Lula and may include messages not specific to this job/maintenance.';

const ThreadSkeleton: React.FC = () => (
	<div className="tenant-texts__skeleton">
		<div className="common-loading w-44 mb-2" />
		<div className="common-loading w-28 mb-2" />
		<div className="common-loading mb-2" />
		<div className="common-loading w-36 mb-2" />
		<div className="common-loading w-44 mb-2" />
	</div>
);

const extractContacts = (payload: unknown): TenantContact[] => {
	if (
		payload &&
		typeof payload === 'object' &&
		Array.isArray((payload as TenantContactsResponse).tenant_contacts)
	) {
		return (payload as TenantContactsResponse).tenant_contacts;
	}
	return [];
};

const extractMessages = (
	payload: unknown,
): {
	messages: TenantContactMessage[];
	currentPage: number;
	lastPage: number;
} => {
	if (
		payload &&
		typeof payload === 'object' &&
		Array.isArray((payload as TenantContactMessagesResponse).data)
	) {
		const p = payload as TenantContactMessagesResponse;
		return {
			messages: p.data,
			currentPage: p.current_page ?? 1,
			lastPage: p.last_page ?? 1,
		};
	}
	return { messages: [], currentPage: 1, lastPage: 1 };
};

const TenantLulaChat: React.FC = () => {
	const dispatch = useAppDispatch();

	const serviceRequestId = useAppSelector(
		(state) => state?.jobDetails?.slideOut?.srId,
	);
	const jobTimezone = useAppSelector((state) => state?.jobDetails?.data?.timezone);
	const [contacts, setContacts] = useState<TenantContact[]>([]);
	const [activeContactIndex, setActiveContactIndex] = useState(0);
	const [messages, setMessages] = useState<TenantContactMessage[]>([]);
	const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1 });
	const [status, setStatus] = useState<TenantTextsStatus>('idle');
	const [isFetchingMore, setIsFetchingMore] = useState(false);

	const contactsAbortRef = useRef<AbortController | null>(null);
	const messagesAbortRef = useRef<AbortController | null>(null);

	const hasMorePages = pagination.currentPage < pagination.lastPage;

	const loadMessages = useCallback(
		(contact: TenantContact, page: number, isAppend: boolean) => {
			if (USE_TENANT_TEXTS_MOCK) {
				setMessages(MOCK_MESSAGES);
				setPagination({ currentPage: 1, lastPage: 1 });
				setStatus('loaded');
				return;
			}
			messagesAbortRef.current?.abort();
			const controller = createAbortSignal();
			messagesAbortRef.current = controller;

			if (!isAppend) setStatus('loading');

			dispatch(
				fetchTenantContactMessages(
					serviceRequestId,
					contact.tenant_contact_id,
					controller.signal,
				)({ page }),
			)
				.unwrap()
				.then((payload: unknown) => {
					const {
						messages: newMessages,
						currentPage,
						lastPage,
					} = extractMessages(payload);
					// API returns newest-first; keep newest-first for column-reverse rendering.
					// Older pages append to array end = DOM end = visual top in column-reverse.
					if (isAppend) {
						setMessages((prev) => [...prev, ...newMessages]);
					} else {
						setMessages(newMessages);
					}
					setPagination({ currentPage, lastPage });
					if (!isAppend) setStatus('loaded');
					setIsFetchingMore(false);
				})
				.catch((error: Record<string, unknown>) => {
					if (error?.actionType === 'canceled') return;
					if (!isAppend) setStatus('error');
					setIsFetchingMore(false);
					toast((error?.message as string) || 'Something went wrong', {
						type: 'error',
					});
				});
		},
		[dispatch, serviceRequestId],
	);

	const loadContacts = useCallback(() => {
		if (USE_TENANT_TEXTS_MOCK) {
			setContacts(MOCK_CONTACTS);
			setActiveContactIndex(0);
			setMessages(MOCK_MESSAGES);
			setPagination({ currentPage: 1, lastPage: 1 });
			setStatus('loaded');
			return;
		}
		if (!serviceRequestId) return;
		contactsAbortRef.current?.abort();
		const controller = createAbortSignal();
		contactsAbortRef.current = controller;

		setStatus('loading');
		dispatch(fetchTenantContacts(serviceRequestId, controller.signal)({}))
			.unwrap()
			.then((payload: unknown) => {
				const nextContacts = extractContacts(payload);
				setContacts(nextContacts);
				setActiveContactIndex(0);
				if (nextContacts.length > 0) {
					loadMessages(nextContacts[0], 1, false);
				} else {
					setStatus('loaded');
				}
			})
			.catch((error: Record<string, unknown>) => {
				if (error?.actionType === 'canceled') return;
				setStatus('error');
				toast((error?.message as string) || 'Something went wrong', {
					type: 'error',
				});
			});
	}, [dispatch, serviceRequestId, loadMessages]);

	useEffect(() => {
		if (serviceRequestId) {
			loadContacts();
		}
		return () => {
			contactsAbortRef.current?.abort();
			messagesAbortRef.current?.abort();
		};
	}, [serviceRequestId, loadContacts]);

	const handleSelectContact = useCallback(
		(index: number) => {
			setActiveContactIndex(index);
			setMessages([]);
			setPagination({ currentPage: 1, lastPage: 1 });
			if (contacts[index]) {
				loadMessages(contacts[index], 1, false);
			}
		},
		[contacts, loadMessages],
	);

	const handleRetry = useCallback(() => {
		loadContacts();
	}, [loadContacts]);

	const handleLoadMore = useCallback(() => {
		if (!isFetchingMore && hasMorePages) {
			setIsFetchingMore(true);
			const contact = contacts[activeContactIndex];
			if (contact) {
				loadMessages(contact, pagination.currentPage + 1, true);
			}
		}
	}, [
		isFetchingMore,
		hasMorePages,
		contacts,
		activeContactIndex,
		pagination.currentPage,
		loadMessages,
	]);

	// Sentinel div at DOM end = visual top in column-reverse. Fires when user scrolls to top.
	const loadMoreRef = useInfiniteScroll(
		hasMorePages && !isFetchingMore,
		handleLoadMore,
		'',
		'100px',
		0.2,
	);

	const activeContact = contacts[activeContactIndex];
	const showSidebar = contacts.length >= 2;

	return (
		<div
			className={`tenant-texts${showSidebar ? ' tenant-texts--with-sidebar' : ''}`}
		>
			{showSidebar ? (
				<TenantSidebar
					contacts={contacts}
					activeIndex={activeContactIndex}
					onSelect={handleSelectContact}
				/>
			) : null}
			<div className="tenant-texts__chat">
				<Alert color="none" className="tenant-texts__warning">
					<svg
						width="15"
						height="15"
						viewBox="0 0 15 15"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M7.16667 4.5V7.16667M7.16667 9.83333H7.17333M13.8333 7.16667C13.8333 10.8486 10.8486 13.8333 7.16667 13.8333C3.48477 13.8333 0.5 10.8486 0.5 7.16667C0.5 3.48477 3.48477 0.5 7.16667 0.5C10.8486 0.5 13.8333 3.48477 13.8333 7.16667Z"
							stroke="#D25100"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					{WARNING_COPY}
				</Alert>

				{status === 'loading' ? 
					<div className="d-flex justify-content-center align-items-center h-100" aria-live="polite">
						<Spinner
							size="sm"
							role="status"
							aria-label="Loading older messages"
						/>
					</div>
				: null}

				{status === 'error' ? (
					<div className="tenant-texts__error">
						<p className="tenant-texts__error-text" aria-live="assertive">
							We couldn&apos;t load these messages.
						</p>
						<Button
							type="button"
							color="link"
							className="tenant-texts__retry"
							onClick={handleRetry}
						>
							Retry
						</Button>
					</div>
				) : null}

				{status === 'loaded' && messages.length === 0 ? (
					<div className="tenant-texts__empty-state">
						<PmNoMessageIcon />
						<span>No messages yet</span>
					</div>
				) : null}

				{status === 'loaded' && messages.length > 0 ? (
					<div className="tenant-texts__message-inner">
						<TextThread
							messages={messages}
							contactName={activeContact?.name ?? ''}
							jobTimezone={jobTimezone}
						/>
						{/* DOM last = visual top (column-reverse). Only enters viewport when
						    the user has scrolled up past all loaded messages. */}
						<div
							ref={loadMoreRef}
							className="tenant-texts__load-trigger"
							aria-hidden="true"
						>
							{isFetchingMore ? (
								<Spinner
									size="sm"
									role="status"
									aria-label="Loading older messages"
								/>
							) : null}
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default TenantLulaChat;
